import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Phone, DollarSign, Clock, TrendingUp, Users, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'];

function Dashboard() {
  const [overview, setOverview] = useState(null);
  const [callsTimeseries, setCallsTimeseries] = useState([]);
  const [costTimeseries, setCostTimeseries] = useState([]);
  const [durationTimeseries, setDurationTimeseries] = useState([]);
  const [statusBreakdown, setStatusBreakdown] = useState([]);
  const [hangupBreakdown, setHangupBreakdown] = useState([]);
  const [loading, setLoading] = useState(true);

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
  });

  const fetchData = async () => {
    try {
      setLoading(true);
      const [overviewRes, callsRes, costRes, durationRes, statusRes, hangupRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/analytics/overview`, getAuthHeaders()),
        axios.get(`${BACKEND_URL}/api/analytics/timeseries/calls?days=30`, getAuthHeaders()),
        axios.get(`${BACKEND_URL}/api/analytics/timeseries/cost?days=30`, getAuthHeaders()),
        axios.get(`${BACKEND_URL}/api/analytics/timeseries/duration?days=30`, getAuthHeaders()),
        axios.get(`${BACKEND_URL}/api/analytics/status-breakdown`, getAuthHeaders()),
        axios.get(`${BACKEND_URL}/api/analytics/hangup-breakdown`, getAuthHeaders())
      ]);

      setOverview(overviewRes.data);
      setCallsTimeseries(callsRes.data);
      setCostTimeseries(costRes.data);
      setDurationTimeseries(durationRes.data);
      setStatusBreakdown(statusRes.data);
      setHangupBreakdown(hangupRes.data);
    } catch (error) {
      toast.error('Failed to fetch analytics data');
    } finally {
      setLoading(false);
    }
  };

  const generateMockData = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/dev/generate-mock-data?count=100`, {}, getAuthHeaders());
      toast.success('Mock data generated successfully!');
      fetchData();
    } catch (error) {
      toast.error('Failed to generate mock data');
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  if (loading) {
    return <div className="flex items-center justify-center h-96" data-testid="loading-spinner">Loading analytics...</div>;
  }

  if (!overview) {
    return (
      <div className="flex flex-col items-center justify-center h-96 space-y-4" data-testid="no-data-state">
        <AlertCircle className="h-16 w-16 text-slate-400" />
        <p className="text-slate-600">No execution data available</p>
        <Button onClick={generateMockData} data-testid="generate-mock-btn">Generate Mock Data</Button>
      </div>
    );
  }

  const kpiCards = [
    { title: 'Total Executions', value: overview.total_executions, icon: Phone, color: 'text-blue-600' },
    { title: 'Calls Today', value: overview.calls_today, icon: TrendingUp, color: 'text-green-600' },
    { title: 'Total Cost', value: `$${overview.total_cost}`, icon: DollarSign, color: 'text-yellow-600' },
    { title: 'Avg Duration', value: `${Math.round(overview.avg_duration)}s`, icon: Clock, color: 'text-purple-600' },
    { title: 'Success Rate', value: `${overview.success_rate}%`, icon: TrendingUp, color: 'text-green-600' },
    { title: 'Avg Cost/Call', value: `$${overview.avg_cost_per_call}`, icon: DollarSign, color: 'text-orange-600' }
  ];

  return (
    <div className="space-y-6" data-testid="dashboard-container">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold">Analytics Overview</h1>
        <Button onClick={fetchData} variant="outline" data-testid="refresh-btn">Refresh</Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {kpiCards.map((kpi, idx) => {
          const Icon = kpi.icon;
          return (
            <Card key={idx} data-testid={`kpi-card-${idx}`}>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">{kpi.title}</CardTitle>
                <Icon className={`h-4 w-4 ${kpi.color}`} />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{kpi.value}</div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card data-testid="calls-timeseries-chart">
          <CardHeader>
            <CardTitle>Calls per Day (Last 30 Days)</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={callsTimeseries}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="count" stroke="#3b82f6" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card data-testid="cost-timeseries-chart">
          <CardHeader>
            <CardTitle>Cost per Day (Last 30 Days)</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={costTimeseries}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="cost" fill="#10b981" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card data-testid="duration-timeseries-chart">
          <CardHeader>
            <CardTitle>Avg Duration per Day (Last 30 Days)</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={durationTimeseries}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="avg_duration" stroke="#8b5cf6" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <div className="space-y-6">
          <Card data-testid="status-breakdown-chart">
            <CardHeader>
              <CardTitle>Status Distribution</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={240}>
                <PieChart>
                  <Pie 
                    data={statusBreakdown} 
                    dataKey="count" 
                    nameKey="status" 
                    cx="50%" 
                    cy="45%" 
                    outerRadius={80}
                  >
                    {statusBreakdown.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value, name) => [`${value} calls`, name]} />
                  <Legend verticalAlign="bottom" height={36} />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          <Card data-testid="hangup-breakdown-chart">
            <CardHeader>
              <CardTitle>Hangup Distribution</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={240}>
                <PieChart>
                  <Pie 
                    data={hangupBreakdown} 
                    dataKey="count" 
                    nameKey="hangup_by" 
                    cx="50%" 
                    cy="45%" 
                    outerRadius={80}
                  >
                    {hangupBreakdown.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value, name) => [`${value} calls`, name]} />
                  <Legend verticalAlign="bottom" height={36} />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;
